(function () {
  "use strict";

  const SELECTORS = {
    root: "[data-olivex-purchase]",
    form: "[data-olivex-purchase-form]",
    purchaseOption: "[data-purchase-option]",
    subscriptionControls: "[data-subscription-controls]",
    planOption: "[data-plan-option]",
    quantityInput: "[data-quantity-input]",
    cartVariant: "[data-cart-variant]",
    cartQuantity: "[data-cart-quantity]",
    cartSellingPlan: "[data-cart-selling-plan]",
    totalLabel: "[data-total-label]",
    total: "[data-delivery-total]",
    savings: "[data-delivery-savings]",
    subscriptionUnitPrice: "[data-subscription-unit-price]",
    discountBadge: "[data-discount-badge]",
    submit: "[data-add-to-cart]",
    submitLabel: "[data-submit-label]",
    submitProgress: "[data-submit-progress]",
    status: "[data-cart-status]",
  };

  function number(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clampQuantity(value) {
    return Math.min(12, Math.max(1, Math.round(number(value, 1))));
  }

  function selected(root, selector) {
    return root.querySelector(`${selector}:checked`);
  }

  function moneyFormatter(root) {
    const currency = root.dataset.currency || "NOK";
    const locale = document.documentElement.lang || "nb-NO";

    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: currency === "NOK" ? 0 : 2,
        maximumFractionDigits: 2,
      });
    } catch (_error) {
      return new Intl.NumberFormat("nb-NO", {
        style: "currency",
        currency: "NOK",
        maximumFractionDigits: 2,
      });
    }
  }

  function formatNumber(value, precision, thousands, decimal) {
    const fixed = (number(value) / 100).toFixed(precision);
    const [integer, fraction] = fixed.split(".");
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return precision > 0 ? `${grouped}${decimal}${fraction}` : grouped;
  }

  function formatMoney(root, cents) {
    const format = root.dataset.moneyFormat || "";
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) return moneyFormatter(root).format(number(cents) / 100);

    let amount;
    switch (placeholder[1]) {
      case "amount_no_decimals":
        amount = formatNumber(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        amount = formatNumber(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        amount = formatNumber(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        amount = formatNumber(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        amount = formatNumber(cents, 0, " ", ",");
        break;
      case "amount_with_space_separator":
        amount = formatNumber(cents, 2, " ", ",");
        break;
      case "amount":
      default:
        amount = formatNumber(cents, 2, ",", ".");
        break;
    }

    return format.replace(placeholder[0], amount).trim();
  }

  function announce(root, message, state = "") {
    const status = root.querySelector(SELECTORS.status);
    if (!status) return;
    status.textContent = message;
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  }

  function activeState(root) {
    const purchase = selected(root, SELECTORS.purchaseOption);
    const isSubscription = purchase?.value === "subscription";
    const plan = isSubscription ? selected(root, SELECTORS.planOption) : null;
    const quantityInput = root.querySelector(SELECTORS.quantityInput);
    const quantity = isSubscription ? clampQuantity(quantityInput?.value) : 1;
    const unitPrice = number(isSubscription ? plan?.dataset.unitPrice : purchase?.dataset.unitPrice);
    const basePrice = number(isSubscription ? plan?.dataset.basePrice : purchase?.dataset.basePrice);
    const discount = number(plan?.dataset.discountPercent);

    return {
      purchase,
      isSubscription,
      plan,
      quantity,
      unitPrice,
      basePrice,
      discount,
      variantId: purchase?.dataset.variantId || "",
      sellingPlanId: plan?.value || "",
    };
  }

  function syncForm(root, { announceChange = false } = {}) {
    const form = root.querySelector(SELECTORS.form);
    if (!form) return;

    const state = activeState(root);
    const subscriptionControls = root.querySelector(SELECTORS.subscriptionControls);
    const quantityInput = root.querySelector(SELECTORS.quantityInput);
    const variantInput = form.querySelector(SELECTORS.cartVariant);
    const quantityField = form.querySelector(SELECTORS.cartQuantity);
    const sellingPlanField = form.querySelector(SELECTORS.cartSellingPlan);
    const totalLabel = root.querySelector(SELECTORS.totalLabel);
    const total = root.querySelector(SELECTORS.total);
    const savings = root.querySelector(SELECTORS.savings);
    const subscriptionUnitPrice = root.querySelector(SELECTORS.subscriptionUnitPrice);
    const discountBadge = root.querySelector(SELECTORS.discountBadge);
    const submit = root.querySelector(SELECTORS.submit);
    const requiredDiscount = number(root.dataset.requiredDiscount, 10);

    if (subscriptionControls) subscriptionControls.hidden = !state.isSubscription;
    if (quantityInput) quantityInput.value = String(state.quantity);
    if (variantInput) variantInput.value = state.variantId;
    if (quantityField) quantityField.value = String(state.quantity);
    if (sellingPlanField) sellingPlanField.value = state.isSubscription ? state.sellingPlanId : "";

    const totalPrice = state.unitPrice * state.quantity;
    const totalSavings = Math.max(0, state.basePrice - state.unitPrice) * state.quantity;

    if (totalLabel) totalLabel.textContent = state.isSubscription ? "Per levering" : "Totalt";
    if (total) total.textContent = state.unitPrice > 0 ? formatMoney(root, totalPrice) : "—";
    if (savings) {
      savings.textContent = state.isSubscription && totalSavings > 0
        ? `Du sparer ${formatMoney(root, totalSavings)}`
        : "";
    }
    if (subscriptionUnitPrice && state.isSubscription) {
      subscriptionUnitPrice.textContent = formatMoney(root, state.unitPrice);
    }
    if (discountBadge && state.isSubscription) {
      discountBadge.textContent = `Spar ${state.discount} %`;
    }

    const planValid = !state.isSubscription || Boolean(state.plan);
    const discountValid = !state.isSubscription || state.discount === requiredDiscount;
    const canSubmit = Boolean(state.purchase && state.variantId && state.unitPrice >= 0 && planValid && discountValid);

    if (submit) submit.disabled = !canSubmit;

    if (state.isSubscription && state.plan && !discountValid) {
      announce(root, `Abonnementet må ha ${requiredDiscount} % rabatt i Shopify før det kan kjøpes.`, "error");
    } else if (announceChange && state.purchase) {
      const choiceName = state.isSubscription
        ? `${state.quantity} ${state.quantity === 1 ? "flaske" : "flasker"}, hver ${state.plan?.dataset.planDays || "valgte"} dager`
        : state.purchase.value === "sample" ? "100 ml smaksprøve" : "500 ml enkeltflaske";
      announce(root, `${choiceName} er valgt.`);
    }
  }

  function adjustQuantity(root, delta) {
    const input = root.querySelector(SELECTORS.quantityInput);
    if (!input) return;
    input.value = String(clampQuantity(number(input.value, 1) + delta));
    syncForm(root, { announceChange: true });
  }

  function setBusy(root, busy) {
    const submit = root.querySelector(SELECTORS.submit);
    const label = root.querySelector(SELECTORS.submitLabel);
    const progress = root.querySelector(SELECTORS.submitProgress);
    if (!submit) return;

    submit.setAttribute("aria-busy", busy ? "true" : "false");
    submit.disabled = busy;
    if (label) label.hidden = busy;
    if (progress) progress.hidden = !busy;
  }

  function restoreSubmitFocus(root) {
    const submit = root.querySelector(SELECTORS.submit);
    if (!submit || submit.disabled) return;
    window.requestAnimationFrame(() => submit.focus({ preventScroll: true }));
  }

  async function refreshCartCount() {
    const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll("[data-cart-count]").forEach((target) => {
      target.textContent = String(cart.item_count);
    });
  }

  async function addToCart(event, root) {
    const form = event.currentTarget;
    if (!window.fetch || !window.FormData) return;

    event.preventDefault();
    syncForm(root);

    const state = activeState(root);
    const submit = root.querySelector(SELECTORS.submit);
    if (!submit || submit.disabled || !state.variantId) {
      announce(root, "Velg et tilgjengelig kjøpsalternativ før du fortsetter.", "error");
      selected(root, SELECTORS.purchaseOption)?.focus();
      return;
    }

    setBusy(root, true);
    announce(root, "Legger varen i handlekurven …");

    if (root.dataset.previewCommerce === "true") {
      window.setTimeout(() => {
        document.querySelectorAll("[data-cart-count]").forEach((target) => {
          target.textContent = String(number(target.textContent) + state.quantity);
        });
        announce(root, `${state.quantity} ${state.quantity === 1 ? "vare er" : "varer er"} lagt i den simulerte preview-kurven.`);
        setBusy(root, false);
        syncForm(root);
        restoreSubmitFocus(root);
      }, 320);
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete("purchase_choice");
      formData.delete("subscription_frequency");

      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
        credentials: "same-origin",
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (_error) {
        payload = {};
      }

      if (!response.ok) {
        const error = new Error(payload.description || payload.message || "Varen kunne ikke legges i handlekurven.");
        error.isShopifyResponse = true;
        throw error;
      }

      try {
        await refreshCartCount();
      } catch (_error) {
        // The item is already added. A cart-count refresh must never submit it twice.
      }
      const itemCount = state.quantity;
      announce(root, `${itemCount} ${itemCount === 1 ? "vare er" : "varer er"} lagt i handlekurven.`);
    } catch (error) {
      if (error?.isShopifyResponse) {
        announce(root, error.message, "error");
        setBusy(root, false);
        syncForm(root);
        restoreSubmitFocus(root);
        return;
      }

      announce(root, "Tilkoblingen ble brutt. Vi fortsetter med Shopifys vanlige handlekurv.");
      HTMLFormElement.prototype.submit.call(form);
      return;
    }

    setBusy(root, false);
    syncForm(root);
    restoreSubmitFocus(root);
  }

  function init(root) {
    if (!root || root.dataset.commerceReady === "true") return;
    root.dataset.commerceReady = "true";
    const nativeFallback = root.querySelector("[data-native-purchase-fallback]");

    const form = root.querySelector(SELECTORS.form);
    form?.addEventListener("submit", (event) => addToCart(event, root));

    root.addEventListener("change", (event) => {
      if (event.target.matches(SELECTORS.purchaseOption) || event.target.matches(SELECTORS.planOption)) {
        syncForm(root, { announceChange: true });
      }
      if (event.target.matches(SELECTORS.quantityInput)) {
        event.target.value = String(clampQuantity(event.target.value));
        syncForm(root, { announceChange: true });
      }
    });

    root.addEventListener("input", (event) => {
      if (event.target.matches(SELECTORS.quantityInput)) syncForm(root);
    });

    root.addEventListener("click", (event) => {
      if (event.target.closest("[data-quantity-decrease]")) adjustQuantity(root, -1);
      if (event.target.closest("[data-quantity-increase]")) adjustQuantity(root, 1);
    });

    syncForm(root);
    if (nativeFallback) nativeFallback.hidden = true;
    root.classList.add("is-enhanced");
  }

  function initAll(container = document) {
    if (container.matches?.(SELECTORS.root)) init(container);
    container.querySelectorAll(SELECTORS.root).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
